/**
 * AccessiBe (acsbapp.com) injects `access-widget-ui` hosts with open shadow roots.
 * On the first Tab of a page load it runs a document bubble "skip link trap":
 *   shouldFocus(Tab) → focus skip links, preventDefault, stopImmediatePropagation
 * and toggles `.skip-links.active`, whose `::before` is a full-viewport dimmer
 * (rgba(0,0,0,.3), z-index 2147483647).
 *
 * That dimmer lives in shadow DOM, so page CSS cannot hide `.skip-links.active::before`.
 * Combined with CoreUI `.modal-backdrop` it leaves the UI stuck extra-dark, and it
 * steals the first Tab out of our dialogs (focus looks like close + Skip to content).
 *
 * This guard: (1) stops AccessiBe's bubble Tab handler while a Pass dialog is open,
 * (2) inerts/hides AccessiBe hosts so skip links are not tab stops, (3) clears `.active`.
 */

const ACSB_HOST_SELECTOR = "access-widget-ui, [data-acsb='skipLinks']"
const ACSB_SUPPRESS_ATTR = "data-pass-acsb-modal-inert"
const GUARD_FLAG = "__passAccessibeModalGuard"

let explicitPassDialogCount = 0

const isVisibleElement = (element) => {
  if (!element) return false
  return element.offsetWidth > 0 || element.offsetHeight > 0
}

export const isPassDialogOpen = () => {
  if (explicitPassDialogCount > 0) return true
  if (typeof document === "undefined") return false
  if (document.body?.classList?.contains("modal-open")) return true

  const dialogs = document.querySelectorAll(
    ".modal.show, .modal-v3.show, .v3-action-dialog"
  )
  return Array.from(dialogs).some((element) => {
    if (element.closest?.("access-widget-ui")) return false
    return isVisibleElement(element)
  })
}

const visitShadowRoots = (host, visit) => {
  if (!host) return
  visit(host)
  if (host.shadowRoot) {
    visit(host.shadowRoot)
  }
}

export const deactivateAccessibeSkipOverlay = (host) => {
  visitShadowRoots(host, (root) => {
    root.querySelectorAll?.(".skip-links.active").forEach((node) => {
      node.classList.remove("active")
    })
  })
  const shadowActive = host?.shadowRoot?.activeElement
  if (shadowActive && typeof shadowActive.blur === "function") {
    shadowActive.blur()
  }
}

export const suppressAccessibeUi = () => {
  if (typeof document === "undefined") return
  document.querySelectorAll(ACSB_HOST_SELECTOR).forEach((host) => {
    if (!host.hasAttribute(ACSB_SUPPRESS_ATTR)) {
      host.setAttribute(
        ACSB_SUPPRESS_ATTR,
        host.hasAttribute("inert") ? "kept" : "1"
      )
      host.setAttribute("inert", "")
      host.setAttribute("aria-hidden", "true")
    }
    deactivateAccessibeSkipOverlay(host)
  })
}

export const restoreAccessibeUi = () => {
  if (typeof document === "undefined") return
  document.querySelectorAll(`[${ACSB_SUPPRESS_ATTR}]`).forEach((host) => {
    const previous = host.getAttribute(ACSB_SUPPRESS_ATTR)
    host.removeAttribute(ACSB_SUPPRESS_ATTR)
    host.removeAttribute("aria-hidden")
    if (previous !== "kept") {
      host.removeAttribute("inert")
    }
    deactivateAccessibeSkipOverlay(host)
  })
}

export const syncAccessibeWithPassDialogs = () => {
  if (isPassDialogOpen()) {
    suppressAccessibeUi()
    return
  }
  restoreAccessibeUi()
}

/**
 * Call from dialog open/close so Tab blocking is not delayed until CoreUI
 * adds `.modal.show` / `body.modal-open`.
 */
export const setPassDialogOpen = (isOpen) => {
  if (isOpen) {
    explicitPassDialogCount += 1
  } else {
    explicitPassDialogCount = Math.max(0, explicitPassDialogCount - 1)
  }
  syncAccessibeWithPassDialogs()
}

export const isAccessibeEventTarget = (event) => {
  if (!event) return false
  const path =
    typeof event.composedPath === "function" ? event.composedPath() : []
  const nodes = path.length
    ? path
    : [event.target, event.target?.getRootNode?.()?.host].filter(Boolean)
  return nodes.some((node) => {
    if (!node || node === document || node === window) return false
    const tag = node.tagName
    if (tag === "ACCESS-WIDGET-UI") return true
    if (
      typeof node.getAttribute === "function" &&
      node.getAttribute("data-acsb")
    ) {
      return true
    }
    if (typeof node.closest === "function") {
      return Boolean(node.closest(ACSB_HOST_SELECTOR))
    }
    return false
  })
}

/**
 * AccessiBe registers a document *bubble* keydown listener. Register this on
 * bubble as well, before AccessiBe's script onload, and stopImmediatePropagation
 * so their first-Tab skip trap never runs. Capture must not be used: it would
 * block Tab from reaching the focused control (vue-select, inputs).
 */
export const blockAccessibeSkipTrapOnTab = (event) => {
  if (event?.key !== "Tab") return
  if (!isPassDialogOpen()) return
  event.stopImmediatePropagation()
}

export const initAccessibeModalGuard = () => {
  if (typeof document === "undefined") return
  if (window[GUARD_FLAG]) return
  window[GUARD_FLAG] = true

  document.addEventListener("keydown", blockAccessibeSkipTrapOnTab, false)

  let rafId = 0
  const scheduleSync = () => {
    if (rafId) return
    rafId = window.requestAnimationFrame(() => {
      rafId = 0
      syncAccessibeWithPassDialogs()
    })
  }

  const observer = new MutationObserver(scheduleSync)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class"]
  })
  scheduleSync()
}
